import { Controller, type ControllerRenderProps } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { FileInput, FormField, Label } from '@/modules/forms';
import { actions as messagesActions } from '@/modules/messages';

import { Fields } from '../../../constants';
import type { Base64File } from '../../../types';
import { getBase64EncodedFile, hasExecutableFileExtension } from '../../../utilities';
import { SecretFile } from '../../SecretFile';
import * as felaRules from './FileField.rules';

const handleChange = (val: Base64File | null, field: ControllerRenderProps) => {
    field.onChange(val);
    field.onBlur();
};

export const FileField = () => {
    const name = Fields.FILE;
    const dispatch = useDispatch();

    const label = (
        <Label>
            <FormattedMessage id={`secret.${name}.label`} />
        </Label>
    );

    return (
        <FormField name={name} label={label} customStyle={felaRules.formItem}>
            <Controller
                name={name}
                render={({ field }) => (
                    <FileInput<Base64File>
                        file={field.value}
                        onFile={file => {
                            if (!file) {
                                handleChange(null, field);
                                return;
                            }

                            if (hasExecutableFileExtension(file.name)) {
                                dispatch(
                                    messagesActions.displayWarningMessage({
                                        message: {
                                            id: 'secret.file.executableNotAllowed',
                                        },
                                    }),
                                );
                                handleChange(null, field);
                                return;
                            }

                            getBase64EncodedFile(file)
                                .then(base64 => {
                                    handleChange(base64, field);
                                })
                                .catch(() => {
                                    handleChange(null, field);
                                });
                        }}
                        renderFile={({ onRemove }) => <SecretFile file={field.value} onRemove={onRemove} />}
                    />
                )}
            />
        </FormField>
    );
};
